import { create } from 'zustand';

import { readRefreshToken, readToken, writeRefreshToken, writeToken } from '../lib/storage';

export type UserRole = 'OWNER' | 'ADMIN' | 'MARKETER' | 'ANALYST' | 'DEVELOPER' | 'VIEWER' | null;

export interface AuthUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  emailVerified: boolean;
}

export interface BrandSummary {
  id: string;
  name: string;
  slug: string;
  planId: string | null;
  logoUrl: string | null;
  role: string | null;
}

export interface AuthState {
  user: AuthUser | null;
  token: string | null;
  refreshToken: string | null;
  /** Parent account that groups all brands */
  accountId: string | null;
  /** Currently active brand (Organization) */
  orgId: string | null;
  role: UserRole;
  /** All brands the user can access within their account */
  brands: BrandSummary[];
  setAuth: (input: {
    user: AuthUser;
    token: string;
    refreshToken?: string | null;
    accountId?: string | null;
    orgId: string | null;
    role: UserRole;
  }) => void;
  setBrands: (brands: BrandSummary[]) => void;
  clearAuth: () => void;
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: readToken(),
  refreshToken: readRefreshToken(),
  accountId: null,
  orgId: null,
  role: null,
  brands: [],
  setAuth: ({ user, token, refreshToken, accountId, orgId, role }) => {
    writeToken(token);
    if (refreshToken !== undefined) {
      writeRefreshToken(refreshToken);
    }
    set((state) => ({
      user,
      token,
      refreshToken: refreshToken ?? state.refreshToken,
      accountId: accountId ?? state.accountId,
      orgId,
      role,
    }));
  },
  setBrands: (brands) => set({ brands }),
  clearAuth: () => {
    writeToken(null);
    writeRefreshToken(null);
    set({ user: null, token: null, refreshToken: null, accountId: null, orgId: null, role: null, brands: [] });
  },
  isAuthenticated: () => Boolean(get().token),
}));
